import {
  type Dispatch,
  type State,
  type StateExternalStore,
} from "@client/ImperativeState";
import type { RoundAggregate } from "@engine/round/aggregate";
import { type SProcessOutputWithMatchingAdjustment } from "@lib/algoInterface/runSProcessOnRound";
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from "react";

type EqualityFn<T> = (left: T, right: T) => boolean;

/**
 * Connection status, schema-skew diagnostics, and presence have dedicated
 * consumers outside the round-page state context. Keeping them out of this
 * snapshot preserves the existing behavior where that churn does not wake
 * every round screen.
 */
export type StateContextState = Omit<
  State,
  "socketStatus" | "schemaSkew" | "connectedUserIds"
>;

const emptyStateContextState = {} as StateContextState;
const emptyStateContextStore: StateExternalStore<StateContextState> = {
  subscribe: () => () => {},
  getSnapshot: () => emptyStateContextState,
};

export const StateContext = createContext<
  StateExternalStore<StateContextState>
>(emptyStateContextStore);

export function useStateSelector<T>(
  selector: (state: StateContextState) => T,
  isEqual: EqualityFn<T> = Object.is,
): T {
  const store = useContext(StateContext);
  const cachedSelectionRef = useRef<{ value: T } | null>(null);
  const getSelection = useCallback(() => {
    const next = selector(store.getSnapshot());
    const cached = cachedSelectionRef.current;
    if (cached != null && isEqual(cached.value, next)) {
      return cached.value;
    }
    cachedSelectionRef.current = { value: next };
    return next;
  }, [isEqual, selector, store]);

  return useSyncExternalStore(store.subscribe, getSelection, getSelection);
}

const selectWholeState = (state: StateContextState) => state;

/**
 * Compatibility API for existing round pages. New consumers that only need a
 * slice should prefer useStateSelector so unrelated state changes do not
 * re-render them.
 */
export function useStateContext(): StateContextState {
  return useStateSelector(selectWholeState);
}

const IGNORED_STATE_CONTEXT_KEYS = new Set<keyof State>([
  "socketStatus",
  "schemaSkew",
  "connectedUserIds",
]);

function statesEqualForContext(left: State, right: State): boolean {
  if (Object.is(left, right)) return true;
  const keys = new Set([
    ...(Object.keys(left) as Array<keyof State>),
    ...(Object.keys(right) as Array<keyof State>),
  ]);
  for (const key of keys) {
    if (IGNORED_STATE_CONTEXT_KEYS.has(key)) continue;
    if (
      !Object.prototype.hasOwnProperty.call(left, key) ||
      !Object.prototype.hasOwnProperty.call(right, key) ||
      !Object.is(left[key], right[key])
    ) {
      return false;
    }
  }
  return true;
}

function stateContextSnapshot(state: State): StateContextState {
  const {
    socketStatus: _socketStatus,
    schemaSkew: _schemaSkew,
    connectedUserIds: _connectedUserIds,
    ...contextState
  } = state;
  return contextState;
}

/**
 * Adapts the event-sourcing store to the state historically exposed through
 * StateContext. The returned snapshot keeps its identity when only connection
 * diagnostics or presence change, so store-backing the context is behavior
 * preserving for whole-state consumers.
 */
export function createStateContextStore(
  source: StateExternalStore,
): StateExternalStore<StateContextState> {
  let previousSource: State | null = null;
  let previousSnapshot: StateContextState | null = null;

  const getSnapshot = () => {
    const nextSource = source.getSnapshot();
    if (
      previousSource != null &&
      previousSnapshot != null &&
      statesEqualForContext(previousSource, nextSource)
    ) {
      previousSource = nextSource;
      return previousSnapshot;
    }

    previousSource = nextSource;
    previousSnapshot = stateContextSnapshot(nextSource);
    return previousSnapshot;
  };

  return {
    subscribe: source.subscribe,
    getSnapshot,
  };
}

export const OutputContext =
  createContext<SProcessOutputWithMatchingAdjustment | null>(null);
export const OutputRoundContext = createContext<RoundAggregate | null>(null);

// The round context defers the algorithm's first run until after first
// paint, so the context is briefly null on load (and after a round change
// while a recompute is pending). Consumers render their no-allocations /
// pending state for that window — see `useSProcessResults` for the model —
// or, for click handlers that can pay the cost on demand (the settings
// export), compute through the shared `sProcessOutputForRound` cache.
export function useOutputContext() {
  return useContext(OutputContext);
}

export function useOutputRoundContext() {
  return useContext(OutputRoundContext);
}

// Dispatch context lives here with the state context: both are imported by
// deep components (note editor, deep-link surfaces), and importing them
// through Round.tsx's re-export drags the whole page module into a cycle
// (the Linux-CI "Cannot access X before initialization" class).
export const DispatchContext = createContext<Dispatch>(
  (() => new Promise(() => {})) as Dispatch,
);
export const useDispatchContext = () => useContext(DispatchContext);
